import type { AirlineItem, AirlineLogo, AirlineLogoUploadContentType, AirlineLogoUploadStart, Paginated } from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { putPresigned } from "../files/upload";
import { api } from "./client";
import { queryClient } from "./query-client";

/**
 * Airlines (0087): Game Settings › Airlines (ADMIN reads, SUPER_ADMIN writes)
 * and the logos every flight card draws (`GET /airlines/logos`, any role).
 */
export interface AirlinesListQuery {
	page: number;
	page_size: number;
	q?: string | undefined;
	has_logo?: "true" | "false" | undefined;
	is_active?: "true" | "false" | undefined;
}

export interface CreateAirlineInput {
	iata_code: string;
	icao_code: string | null;
	name: string;
	country_code: string | null;
	is_active: boolean;
	reason?: string;
}

export interface UpdateAirlineInput {
	name?: string;
	icao_code?: string | null;
	country_code?: string | null;
	is_active?: boolean;
	version: number;
	reason?: string;
}

export const airlineKeys = {
	all: ["airlines"] as const,
	list: (q: AirlinesListQuery) => ["airlines", "list", q] as const,
	/** `codes` = the sorted, de-duplicated set joined by commas (`airlineLogoCodes`). */
	logos: (codes: string) => ["airlines", "logos", codes] as const,
};

/** `GET /airlines` — one offset page + total in one statement. */
export const airlinesListOptions = (q: AirlinesListQuery) =>
	queryOptions({
		queryKey: airlineKeys.list(q),
		queryFn: ({ signal }) => api.get<Paginated<AirlineItem>>("/airlines", { query: { ...q }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 60_000,
	});

/**
 * `GET /airlines/logos?codes=` — ONE request per distinct code set. The URLs
 * are presigned on a floored window (FILE_PHOTO_URL_TTL_SECONDS, 10 min by
 * default), so five minutes stale keeps every URL valid while it is shown.
 */
export const airlineLogosOptions = (codes: string) =>
	queryOptions({
		queryKey: airlineKeys.logos(codes),
		queryFn: ({ signal }) => api.get<AirlineLogo[]>("/airlines/logos", { query: { codes }, signal }),
		staleTime: 5 * 60_000,
		gcTime: 10 * 60_000,
	});

export const invalidateAirlines = () => queryClient.invalidateQueries({ queryKey: airlineKeys.all });

export const createAirline = (body: CreateAirlineInput) => api.post<AirlineItem>("/airlines", body);

export const updateAirline = (id: string, body: UpdateAirlineInput) => api.patch<AirlineItem>(`/airlines/${id}`, body);

/** Presigned PUT, then complete (the server sniffs, re-encodes to PNG and swaps the logo). */
export async function uploadAirlineLogo(input: { airlineId: string; file: File; onProgress?: (fraction: number) => void; signal?: AbortSignal }): Promise<AirlineItem> {
	const { airlineId, file, onProgress, signal } = input;
	const begun = await api.post<AirlineLogoUploadStart>(
		`/airlines/${airlineId}/logo/upload-url`,
		{ content_type: file.type as AirlineLogoUploadContentType, size_bytes: file.size },
		{ signal },
	);
	onProgress?.(0);
	await putPresigned(begun, file, onProgress, signal);
	return api.post<AirlineItem>(`/airlines/${airlineId}/logo/complete`, { upload_token: begun.upload_token }, { signal });
}

export const deleteAirlineLogo = (airlineId: string, reason: string) => api.delete<AirlineItem>(`/airlines/${airlineId}/logo`, undefined, { query: { reason } });
